import { getOr, isNull, isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { ANALYSIS_MARGIN, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER, useSplitView } from '../../../../hooks/useSplitView';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { AnalysisView, SelectedDocument } from '../../../documents/store/types';
import { Analysis, Tabs } from '../../../shared/analysis/Analysis';
import { AnalysisSideMenu } from '../../../shared/analysis/AnalysisSideMenu';
import { Spinner } from '../../../shared/spinner/Spinner';
import { getIsOpening, getMLTestDocument, getMLTestDocumentAnalysisView, getMLTestDocumentUrl, getSelectedPage, resetMLTestDocument, setSchedulePage, toggleMLTestDocumentAnalysisView } from './store';
import styles from './AIPlayground.module.scss';
import { MLTestDocumentPlayground } from './MLTestDocumentPlayground';
import { DocumentViewerType, resetPdfText } from '../../../documents/document-viewer/store';
import { getIsFetchingDocumentQueries } from '../queries/store/selectors';
import { fetchAgreementTypeQueriesStarted } from '../queries/store/actions';

export const MLTestDocumentAnalysis: React.FC = () => {
    const [screenWidth, screenHeight] = useWindowResize();
    const dispatch = useAppDispatch();

    const mlTestDocument = useAppSelector(getMLTestDocument);
    const mlTestDocumentUrl = useAppSelector(getMLTestDocumentUrl);
    const isOpening = useAppSelector(getIsOpening);
    const mlTestDocumentAnalysisView = useAppSelector(getMLTestDocumentAnalysisView);
    const isFetchingQueries = useAppSelector(getIsFetchingDocumentQueries);
    const selectedPage = useAppSelector(getSelectedPage);
    const [pageWidth, pageHeight] = useSplitView(mlTestDocumentAnalysisView, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER);

    const scheduledStartPage = useMemo(() => !isNull(selectedPage) ? selectedPage : undefined, [selectedPage]);

    const toggleAnalysisView = useCallback((view: AnalysisView) => view !== mlTestDocumentAnalysisView && dispatch(toggleMLTestDocumentAnalysisView(view)), [dispatch, mlTestDocumentAnalysisView]);

    const tabOptions = useMemo<Tabs[]>(() => [
        {
            title: getOr('', 'documentDescription', mlTestDocument),
            onClick: noop,
            selected: true,
        },
        {
            title: '',
            onClick: noop,
            selected: false,
        }
    ], [mlTestDocument]);

    const analysisWrapperDimensions = { height: `${screenHeight - NAV_BAR - ANALYSIS_MARGIN}px`, width: `${screenWidth}px` };
    const pageDimensions = { height: `${pageHeight}px`, width: `${pageWidth}px` };

    const mlAgreementTypeId = useMemo(() => mlTestDocument?.agreementTypeId, [mlTestDocument]);

    const setSelectedPage = useCallback((page: number | null) => dispatch(setSchedulePage(page)), [dispatch]);

    useEffect(() => {
        if (!isUndefined(mlAgreementTypeId)) {
            dispatch(fetchAgreementTypeQueriesStarted(mlAgreementTypeId, false));
        }
    }, [dispatch, mlAgreementTypeId]);

    useEffect(() => () => {
        dispatch(resetMLTestDocument());
        dispatch(resetPdfText());
    }, [dispatch]);

    if (isFetchingQueries) {
        return <Spinner />;
    }

    return (
        <div className={styles.analysisWrapper} style={analysisWrapperDimensions}>
            <AnalysisSideMenu toggleAnalysisView={toggleAnalysisView} currentView={mlTestDocumentAnalysisView} isAIPlayground />
            <div className={styles.splitWrapper}>
                {mlTestDocumentAnalysisView !== AnalysisView.DATASET &&
                    <div className={styles.mlTestDocumentAnalysisWrapper} style={pageDimensions}>
                        <Analysis
                            selected={mlTestDocument}
                            url={mlTestDocumentUrl}
                            documentInView={SelectedDocument.ORIGINAL}
                            isOpening={isOpening}
                            tabOptions={tabOptions}
                            documentAnalysisView={mlTestDocumentAnalysisView}
                            selectedPage={selectedPage}
                            showScheduleStartPage={true}
                            scheduleStartPage={scheduledStartPage}
                            setSelectedPage={setSelectedPage}
                            width={pageWidth - 2}
                            type={DocumentViewerType.AI_DOCUMENT_ANALYSIS}
                        />
                    </div>
                }
                {mlTestDocumentAnalysisView !== AnalysisView.DOCUMENT &&
                    <div className={styles.rightSideWrapper} style={pageDimensions}>
                        <MLTestDocumentPlayground mlTestDocument={mlTestDocument!} />
                    </div>
                }
            </div>
        </div>
    );
};
